import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Mail, Copy, Check, Send, Eye, Users, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { serviceProviders } from '../data/serviceProviders';
import { affiliateEmailTemplates, personalizeEmail, followUpTemplate } from '../data/emailTemplates';

const API = process.env.REACT_APP_BACKEND_URL;

const AffiliateOutreach = () => {
  const [selectedCategory, setSelectedCategory] = useState('staging');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, email: null });
  const [copied, setCopied] = useState(false);
  const [contactedProviders, setContactedProviders] = useState(new Set());
  const [declinedProviders, setDeclinedProviders] = useState(new Set());
  const [sending, setSending] = useState(false);
  
  // Form data for personalization
  const [contactName, setContactName] = useState('');
  const [yourPhone, setYourPhone] = useState('(817) 320-5334');
  const [yourEmail, setYourEmail] = useState('larry@larryscala.com');
  
  // Email address input for the provider
  const [providerEmail, setProviderEmail] = useState('');

  // Load saved data from localStorage on mount
  useState(() => {
    const contacted = localStorage.getItem('contactedProviders');
    const declined = localStorage.getItem('declinedProviders');
    if (contacted) setContactedProviders(new Set(JSON.parse(contacted)));
    if (declined) setDeclinedProviders(new Set(JSON.parse(declined)));
  }, []);

  const categories = [
    { id: 'staging', label: 'Home Staging', count: serviceProviders.staging.length },
    { id: 'photography', label: 'Photography', count: serviceProviders.photography.length },
    { id: 'painting', label: 'Painting', count: serviceProviders.painting.length },
    { id: 'cleaning', label: 'Cleaning', count: serviceProviders.cleaning.length },
    { id: 'handyman', label: 'Handyman', count: serviceProviders.handyman.length },
    { id: 'landscaping', label: 'Landscaping', count: serviceProviders.landscaping.length },
    { id: 'titleCompanies', label: 'Title Companies', count: serviceProviders.titleCompanies.length },
    { id: 'attorneys', label: 'Attorneys', count: serviceProviders.attorneys.length },
    { id: 'homeInspectors', label: 'Home Inspectors', count: serviceProviders.homeInspectors.length }
  ];

  const generateEmail = (provider, isFollowUp = false) => {
    const template = isFollowUp ? followUpTemplate : affiliateEmailTemplates[selectedCategory] || affiliateEmailTemplates.general;
    
    const personalizedEmail = personalizeEmail(template, {
      contactName: contactName || 'Team',
      companyName: provider.name,
      yourPhone: yourPhone,
      yourEmail: yourEmail,
      serviceType: categories.find(c => c.id === selectedCategory)?.label || 'Service'
    });

    return personalizedEmail;
  };

  const openEmailPreview = (provider) => {
    setSelectedProvider(provider);
    setProviderEmail(''); // Reset email input
    const email = generateEmail(provider);
    setEmailDialog({ open: true, email });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Email copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyFullEmail = () => {
    const fullEmail = `To: ${providerEmail || '[Enter Email]'}\nSubject: ${emailDialog.email.subject}\n\n${emailDialog.email.body}`;
    copyToClipboard(fullEmail);
  };

  const sendEmailDirectly = async () => {
    if (!providerEmail || !providerEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/send-affiliate-email`,
        {
          to_email: providerEmail,
          to_name: contactName || 'Team',
          company_name: selectedProvider.name,
          subject: emailDialog.email.subject,
          body: emailDialog.email.body,
          category: selectedCategory
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(`Email sent to ${selectedProvider.name}!`);
        markAsContacted(selectedProvider.name);
        setEmailDialog({ open: false, email: null });
        setProviderEmail('');
      }
    } catch (error) {
      console.error('Email send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const markAsContacted = (providerId) => {
    const newSet = new Set(contactedProviders);
    newSet.add(providerId);
    setContactedProviders(newSet);
    localStorage.setItem('contactedProviders', JSON.stringify([...newSet]));
    toast.success('Marked as contacted');
  };

  const markAsDeclined = (providerId) => {
    const newSet = new Set(declinedProviders);
    newSet.add(providerId);
    setDeclinedProviders(newSet);
    localStorage.setItem('declinedProviders', JSON.stringify([...newSet]));
    toast.success('Marked as declined - hidden from list');
  };

  const undoDecline = (providerId) => {
    const newSet = new Set(declinedProviders);
    newSet.delete(providerId);
    setDeclinedProviders(newSet);
    localStorage.setItem('declinedProviders', JSON.stringify([...newSet]));
    toast.success('Provider restored to list');
  };

  const openInGmail = () => {
    const subject = encodeURIComponent(emailDialog.email.subject);
    const body = encodeURIComponent(emailDialog.email.body);
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`, '_blank');
  };

  const allProviders = serviceProviders[selectedCategory] || [];
  const currentProviders = allProviders.filter(p => !declinedProviders.has(p.name));
  const contactedCount = currentProviders.filter(p => contactedProviders.has(p.name)).length;
  const declinedCount = allProviders.filter(p => declinedProviders.has(p.name)).length;

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Affiliate Outreach Manager</h2>
            <p className="text-gray-600 mb-4">
              Generate personalized emails to establish affiliate partnerships with service providers
            </p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-700">Total Providers:</span>
                <span className="ml-2 text-2xl font-bold text-blue-600">70+</span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Contacted:</span>
                <span className="ml-2 text-2xl font-bold text-green-600">{contactedProviders.size}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Declined:</span>
                <span className="ml-2 text-2xl font-bold text-red-600">{declinedProviders.size}</span>
              </div>
            </div>
          </div>
          <Mail className="w-12 h-12 text-blue-600" />
        </div>
      </Card>

      {/* Personalization Settings */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">📝 Personalization Settings</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name (Optional)</label>
            <Input
              placeholder="e.g., John Smith"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank for generic greeting</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Phone</label>
            <Input
              value={yourPhone}
              onChange={(e) => setYourPhone(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Email</label>
            <Input
              value={yourEmail}
              onChange={(e) => setYourEmail(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* Category Selection */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">🎯 Select Service Category</h3>
        <div className="grid grid-cols-3 gap-3">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                selectedCategory === category.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="font-semibold text-gray-900">{category.label}</div>
              <div className="text-sm text-gray-600 mt-1">{category.count} providers</div>
              {contactedProviders.size > 0 && (
                <div className="text-xs text-green-600 mt-1">
                  {serviceProviders[category.id]?.filter(p => contactedProviders.has(p.name)).length || 0} contacted
                </div>
              )}
            </button>
          ))}
        </div>
      </Card>

      {/* Provider List */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            {categories.find(c => c.id === selectedCategory)?.label} Providers
          </h3>
          <div className="text-sm text-gray-600">
            {contactedCount} / {currentProviders.length} contacted • {declinedCount} hidden
          </div>
        </div>
        
        {declinedCount > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-900">
              <strong>{declinedCount} provider{declinedCount > 1 ? 's' : ''} hidden</strong> (declined or no affiliate program). 
              Scroll to bottom to view and restore if needed.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {currentProviders.map((provider, index) => {
            const isContacted = contactedProviders.has(provider.name);
            return (
              <div
                key={index}
                className={`border rounded-lg p-4 ${
                  isContacted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-gray-900">{provider.name}</h4>
                      {isContacted && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-semibold">
                          ✓ Contacted
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{provider.description}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <a
                        href={provider.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {provider.website}
                      </a>
                      {provider.referralBonus && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                          💰 {provider.referralBonus}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <a
                        href={`${provider.website}/contact`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                      >
                        📝 Contact Form
                      </a>
                      <a
                        href={`https://hunter.io/search/${provider.website.replace('https://', '').replace('http://', '').split('/')[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200"
                      >
                        🔍 Find Email (Hunter.io)
                      </a>
                      <a
                        href={`https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(provider.name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded hover:bg-indigo-200"
                      >
                        💼 LinkedIn Search
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEmailPreview(provider)}
                      className="flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                      Preview Email
                    </Button>
                    {!isContacted && (
                      <Button
                        size="sm"
                        onClick={() => markAsContacted(provider.name)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markAsDeclined(provider.name)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Declined Providers Section */}
        {declinedCount > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-300">
            <h4 className="text-md font-semibold text-gray-700 mb-3">
              🚫 Declined / No Affiliate Program ({declinedCount})
            </h4>
            <div className="space-y-2">
              {allProviders.filter(p => declinedProviders.has(p.name)).map((provider, index) => (
                <div key={index} className="bg-gray-100 border border-gray-300 rounded-lg p-3 opacity-60">
                  <div className="flex justify-between items-center">
                    <div>
                      <h5 className="font-semibold text-gray-700 text-sm">{provider.name}</h5>
                      <p className="text-xs text-gray-600">{provider.website}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => undoDecline(provider.name)}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      Restore
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Email Preview Dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(open) => {
        if (!open) {
          setEmailDialog({ open: false, email: null });
          setProviderEmail('');
        }
      }}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview - {selectedProvider?.name}</DialogTitle>
          </DialogHeader>
          
          {emailDialog.email && (
            <div className="space-y-4">
              {/* Email Address Input */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📧 Recipient Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="Enter the company's email address..."
                  value={providerEmail}
                  onChange={(e) => setProviderEmail(e.target.value)}
                  className="border-orange-300 focus:border-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use Hunter.io or the company website to find the correct email
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subject:</label>
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  <p className="text-sm text-gray-900">{emailDialog.email.subject}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Body:</label>
                <div className="bg-gray-50 p-4 rounded border border-gray-200 max-h-64 overflow-y-auto">
                  <pre className="text-sm text-gray-900 whitespace-pre-wrap font-sans">
                    {emailDialog.email.body}
                  </pre>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-900">
                  <strong>💡 Pro Tip:</strong> Personalize further by researching the company and adding specific details about their work or recent projects.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setEmailDialog({ open: false, email: null });
                setProviderEmail('');
              }}
            >
              Close
            </Button>
            <Button
              variant="outline"
              onClick={copyFullEmail}
              className="flex items-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Copy to Clipboard
            </Button>
            <Button
              onClick={openInGmail}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Open in Gmail
            </Button>
            <Button
              onClick={sendEmailDirectly}
              disabled={!providerEmail || sending}
              className="bg-orange-600 hover:bg-orange-700 flex items-center gap-2"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send via TurboSMTP
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliateOutreach;
